import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, ArrowRight, PartyPopper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const API = "/api";

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (sessionId) {
      pollStatus(0);
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollStatus = async (attempt) => {
    if (attempt >= 5) {
      setStatus('timeout');
      return;
    }
    try {
      const res = await axios.get(`${API}/subscriptions/status/${sessionId}`);
      setDetails(res.data);
      if (res.data.payment_status === 'paid') {
        setStatus('success');
      } else if (res.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollStatus(attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(attempt + 1), 2000);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="subscription-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        <Card className="bg-card/60 backdrop-blur border-border">
          <CardContent className="p-8 text-center space-y-4">
            {status === 'checking' && (
              <>
                <Loader2 size={48} className="mx-auto text-purple-400 animate-spin" />
                <h2 className="text-xl font-bold">Processing your payment...</h2>
                <p className="text-sm text-muted-foreground">Please wait while we confirm your subscription.</p>
              </>
            )}
            {status === 'success' && (
              <>
                <div className="relative">
                  <CheckCircle size={56} className="mx-auto text-green-500" />
                  <PartyPopper size={24} className="absolute top-0 right-1/3 text-yellow-400 animate-bounce" />
                </div>
                <h2 className="text-2xl font-bold">Welcome aboard!</h2>
                <p className="text-muted-foreground">
                  <strong>{details?.business_name}</strong> is now on the <strong>{details?.tier_name}</strong> plan.
                </p>
                <p className="text-sm text-muted-foreground">
                  Your listing has been upgraded. Thank you for supporting the Twin Cities music community!
                </p>
                <div className="pt-4 space-y-2">
                  <Link to="/">
                    <Button className="w-full bg-purple-600 hover:bg-purple-500 gap-2">
                      Back to Twin Tube <ArrowRight size={16} />
                    </Button>
                  </Link>
                </div>
              </>
            )}
            {(status === 'error' || status === 'expired' || status === 'timeout') && (
              <>
                <XCircle size={48} className="mx-auto text-red-400" />
                <h2 className="text-xl font-bold">
                  {status === 'timeout' ? 'Still processing' : 'Something went wrong'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {status === 'timeout' 
                    ? 'Your payment may still be processing. Check your email for confirmation.' 
                    : 'The payment could not be confirmed. Please try again.'}
                </p>
                <Link to="/pricing">
                  <Button variant="outline" className="gap-2">
                    Back to Pricing <ArrowRight size={16} />
                  </Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
